/**
 * useAvatarConfig - Centralized configuration parsing hook
 *
 * Extracts all configuration parsing from App.tsx to provide a clean,
 * typed config object. Handles props, query params, and env vars.
 *
 * @refactored January 2026 - Phase 0 light refactoring
 */

import { useMemo } from 'react';
import type { AppProps } from '../appTypes';

/**
 * Resolved default URLs based on hostname
 */
const CAMLIN_TLDS = ['camlinconnect.com.au', 'camlinconnect.co.uk', 'camlinconnect.com'];
const RUNTIME_HOST_PATHS = new Set(['/live', '/visual']);

function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed.length > 0 ? trimmed : '/';
}

function sameOriginUrl(pathname: string): string {
  return new URL(pathname, window.location.origin).toString();
}

function sameOriginWebSocketUrl(pathname: string): string {
  const url = new URL(pathname, window.location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

/**
 * Safely read a Vite-style env var. `import.meta.env` is a Vite-only
 * construct; in webpack/CRA hosts (e.g. nua-workflow) `import.meta.env`
 * is undefined, so a bare `import.meta.env.VITE_FOO` access throws
 * `Cannot read properties of undefined (reading 'VITE_FOO')` and
 * crashes the SDK panel before any UI renders. This helper returns
 * `undefined` instead, allowing the surrounding `||` fallbacks to
 * resolve without bringing down the host app.
 */
function viteEnv(key: string): string | undefined {
  if (typeof import.meta === 'undefined') return undefined;
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env ? env[key] : undefined;
}

function resolveCamlinPlatformOrigin(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const hostname = window.location.hostname;
  if (hostname.startsWith('sig')) {
    return 'https://sig.camlinconnect.com.au';
  }

  for (const tld of CAMLIN_TLDS) {
    if (hostname === tld || hostname.endsWith(`.${tld}`)) {
      return `https://${tld}`;
    }
  }

  return null;
}

const resolveDefaultUrls = (pathname: string) => {
  if (RUNTIME_HOST_PATHS.has(pathname)) {
    return {
      runtimeUrl: trimTrailingSlash(sameOriginUrl('/runtime-api')),
      voiceUrl: trimTrailingSlash(sameOriginUrl('/voice-api')),
      ttsUrl: trimTrailingSlash(sameOriginUrl('/runtime-api')),
      sipWebSocketUrl: pathname === '/live' ? sameOriginWebSocketUrl('/sip-ws/ws') : undefined
    };
  }

  const platformOrigin = resolveCamlinPlatformOrigin();
  const runtimeUrl = trimTrailingSlash(
    platformOrigin ||
    viteEnv('VITE_CAMLIN_URL') ||
    'https://camlinconnect.com.au'
  );
  const voiceUrl = trimTrailingSlash(
    viteEnv('VITE_CAMLIN_VOICE_URL') ||
    `${runtimeUrl}/voice-api`
  );
  const isLocalDevelopment =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '0.0.0.0';
  const isStandaloneAvatarHost = window.location.hostname.startsWith('avatar.camlinconnect.');

  return {
    runtimeUrl,
    voiceUrl,
    ttsUrl: isLocalDevelopment
      ? 'http://localhost:5075'
      : isStandaloneAvatarHost
        ? ''
        : 'https://avatar.camlinconnect.com.au',
    sipWebSocketUrl: undefined
  };
};

/**
 * AvatarConfig - All configuration values needed by the avatar
 */
export interface AvatarConfig {
  // Identity
  projectToken: string;
  handoffToken: string;
  liveDestination: string;

  // URLs
  runtimeUrl: string;
  voiceUrl: string;
  sipWebSocketUrl?: string;
  ttsUrl: string;

  // UI Options
  showChat: boolean;
  showLogo: boolean;
  showButtons: boolean;
  showSip: boolean;

  // Behavior
  enableSentiment: boolean;
  activeListen: boolean;
  isVisualOnly: boolean;
  cameraZoom: number;

  // Modes
  isBuilderMode: boolean;
  builderDiagramId: string;
  isDesignAssistant: boolean;
  sdkMode: boolean;

  // Call modes (initial values from props/URL)
  initialIsWebrtcCall: boolean;
  initialIsMobileCall: boolean;
}

/**
 * Parse a boolean from a query param string or prop
 */
function parseBooleanParam(
  propValue: boolean | undefined,
  queryValue: string | null,
  defaultValue: boolean
): boolean {
  if (typeof propValue === 'boolean') {
    return propValue;
  }
  if (queryValue === 'true') return true;
  if (queryValue === 'false') return false;
  return defaultValue;
}

/**
 * useAvatarConfig - Parse all configuration from props and query params
 *
 * @param props - Props passed to App component
 * @returns AvatarConfig object with all parsed values
 */
export function useAvatarConfig(props: AppProps = {}): AvatarConfig {
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const normalizedPathname = useMemo(() => normalizePathname(window.location.pathname), []);
  const isRuntimeHostRoute = RUNTIME_HOST_PATHS.has(normalizedPathname);
  const isRuntimeHostVisualRoute = normalizedPathname === '/visual';
  const isLocalRuntimeHostLiveRoute = useMemo(() => (
    normalizedPathname === '/live' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ), [normalizedPathname]);
  const defaultUrls = useMemo(() => resolveDefaultUrls(normalizedPathname), [normalizedPathname]);

  return useMemo(() => {
    const liveModeRequested =
      props.forceLiveMode === true ||
      isRuntimeHostRoute ||
      queryParams.get('mode') === 'live' ||
      queryParams.get('live') === 'true';
    const requestedLiveSessionKind =
      queryParams.get('liveSessionKind') ||
      (queryParams.get('contextualLive') === 'true' ? 'contextual-chat' : null)

    const explicitProjectToken =
      props.projectToken ||
      queryParams.get('pt') ||
      viteEnv('VITE_PROJECT_TOKEN') ||
      '';
    const explicitBuilderDiagramId =
      props.builderDiagramId ||
      queryParams.get('did') ||
      queryParams.get('diagramId') ||
      '';
    const useLocalLiveSmokeDefaults =
      isLocalRuntimeHostLiveRoute &&
      liveModeRequested &&
      requestedLiveSessionKind !== 'contextual-chat' &&
      explicitProjectToken.length === 0 &&
      explicitBuilderDiagramId.length === 0;

    // Identity tokens
    const projectToken = explicitProjectToken || (useLocalLiveSmokeDefaults ? 'test-lab' : '');

    const handoffToken = props.handoffToken ||
      queryParams.get('ht') ||
      viteEnv('VITE_HANDOFF_TOKEN') ||
      '';
    // CRITICAL: do NOT default to 'avatar-live-mode' here. That default was
    // the silent reason AgentLink and Decision Memory were stuck — every
    // host (even browser-only contextual-chat ones that have no SIP
    // destination at all) ended up with `hasVoiceDestination=true` →
    // `liveVoiceCallEnabled=true` → `useLiveVisualChannel` skipped the
    // `/session-stream` bootstrap → `/visual-channel` immediately closed
    // with `session-not-found` because no runtime session existed for the
    // handoff token. The host that actually wants the
    // `avatar-live-mode` Asterisk dial-plan target (the runtime-host
    // `/live` route serving `visual.camlinconnect.com.au/live`) injects
    // it explicitly via `buildLiveRouteAppProps`. Hosts that don't set
    // `liveDestination` are browser-only contextual chat and MUST stay
    // on the websocket-only path. Locked down May-1 2026
    // (live-mode reliability fix follow-up — full forensic in
    // `camlin-agentlink/docs/SESSION_*` of the same date).
    const liveDestination = props.liveDestination ||
      queryParams.get('liveDestination') ||
      queryParams.get('liveExt') ||
      viteEnv('VITE_LIVE_DESTINATION') ||
      '';

    // URLs
    const runtimeUrl = trimTrailingSlash(
      props.runtimeUrl ||
      queryParams.get('runtimeUrl') ||
      defaultUrls.runtimeUrl
    );
    const voiceUrl = trimTrailingSlash(
      props.voiceUrl ||
      queryParams.get('voiceUrl') ||
      defaultUrls.voiceUrl
    );
    const sipWebSocketUrl = props.sipWebSocketUrl ||
      queryParams.get('sipWebSocketUrl') ||
      queryParams.get('sipWsUrl') ||
      viteEnv('VITE_SIP_WEBSOCKET_URL') ||
      defaultUrls.sipWebSocketUrl;
    const ttsUrl = queryParams.get('ttsUrl') || props.ttsUrl || defaultUrls.ttsUrl;

    // UI Options - parse with defaults
    const showChat = !parseBooleanParam(props.noChat, queryParams.get('nochat'), isRuntimeHostRoute);
    const showLogo = parseBooleanParam(props.showLogo, queryParams.get('showlogo'), false);
    const showButtons = !parseBooleanParam(props.noButtons, queryParams.get('nobuttons'), false);
    const showSip = !parseBooleanParam(props.noSip, queryParams.get('nosip'), isRuntimeHostVisualRoute);

    // Behavior
    const enableSentiment = parseBooleanParam(
      props.enableSentiment,
      queryParams.get('sentiment') === 'false' ? 'false' : null,
      true // Enabled by default
    );
    const activeListen = parseBooleanParam(props.activeListen, queryParams.get('activelisten'), false);
    const isVisualOnly = parseBooleanParam(props.isVisualOnly, queryParams.get('visual'), isRuntimeHostVisualRoute);
    const cameraZoom = props.cameraZoom ?? parseInt(queryParams.get('zoom') || '2000', 10);

    // Modes
    const isBuilderMode = parseBooleanParam(props.isBuilderMode, queryParams.get('builder'), false);
    const builderDiagramId = explicitBuilderDiagramId || (
      useLocalLiveSmokeDefaults
        ? 'live-mode-speech-smoke'
        : requestedLiveSessionKind === 'contextual-chat'
          ? ''
          : 'main'
    );
    const isDesignAssistant = parseBooleanParam(props.isDesignAssistant, null, false) ||
      queryParams.get('mode') === 'design';
    const sdkMode = parseBooleanParam(props.sdkMode, queryParams.get('sdk'), false);

    // Initial call modes
    const initialIsWebrtcCall = liveModeRequested
      ? false
      : parseBooleanParam(props.isWebrtcCall, queryParams.get('webrtc'), false);
    const initialIsMobileCall = liveModeRequested
      ? false
      : parseBooleanParam(props.isMobileCall, queryParams.get('mobile'), false);

    return {
      projectToken,
      handoffToken,
      liveDestination,
      runtimeUrl,
      voiceUrl,
      sipWebSocketUrl,
      ttsUrl,
      showChat,
      showLogo,
      showButtons,
      showSip,
      enableSentiment,
      activeListen,
      isVisualOnly,
      cameraZoom,
      isBuilderMode,
      builderDiagramId,
      isDesignAssistant,
      sdkMode,
      initialIsWebrtcCall,
      initialIsMobileCall,
    };
  }, [props, queryParams, defaultUrls, isLocalRuntimeHostLiveRoute, isRuntimeHostRoute, isRuntimeHostVisualRoute]);
}

export default useAvatarConfig;
